import {
  EWebinarWithInteractionsFragment,
  EWebinarWithSetFragment,
  FullSetFragment,
  InteractionFragment,
  PublicSetWithEWebinarFragment,
} from '$/GraphQL/schema/types';
import Loading from '$/components/Loading';
import { WebinarPropertiesProvider } from '$/components/Property/PropertyProvider';
import { activateLanguage } from '$/shared/i18n';
import React, { useContext } from 'react';

export interface WebinarState {
  setId: string | 'all';
  action: 'dashboard' | 'edit';

  set: FullSetFragment;
  publicWebinar?: EWebinarWithInteractionsFragment;
  draftWebinar: EWebinarWithSetFragment & { interactions: InteractionFragment[] };

  refetchWebinar: () => void;
}

export const WebinarContext = React.createContext({} as WebinarState);

export const useWebinarContext = (): WebinarState => {
  return useContext(WebinarContext) ?? {};
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isFullSetFragment(set: any): set is FullSetFragment {
  return set && typeof set.draftWebinar !== 'undefined';
}

export const WebinarContextProvider: React.FC<{
  set?: FullSetFragment | PublicSetWithEWebinarFragment;
  refetchSet: () => void;
  action: 'edit' | 'dashboard';
  children?: React.ReactNode;
}> = ({ set, refetchSet, action, children }) => {
  const refetchWebinar = React.useCallback(() => {
    refetchSet();
  }, [refetchSet]);

  const webinarContext: WebinarState = React.useMemo(() => {
    if (!set) return {} as WebinarState;
    return {
      setId: set.id,
      action,

      // These could technically be undefined but we ignore that here and assume
      // downstream code will check if setId == 'all' to expect undefined values
      set,
      draftWebinar: isFullSetFragment(set) ? { ...set.draftWebinar, set } : undefined,
      publicWebinar: set.publicWebinar,

      refetchWebinar,
    } as WebinarState;
  }, [set, action, refetchWebinar]);

  const [doneLoadingLang, setDoneLoadingLang] = React.useState(false);

  const lang =
    (action === 'dashboard' && set?.publicWebinar
      ? set.publicWebinar.viewerRoomSettings.language
      : isFullSetFragment(set)
        ? set?.draftWebinar?.viewerRoomSettings.language
        : undefined) ?? 'en';
  React.useEffect(() => {
    if (!lang) return;

    (async (): Promise<void> => {
      await activateLanguage(lang);
      setDoneLoadingLang(true);
    })();
  }, [lang, setDoneLoadingLang]);

  if (!doneLoadingLang) {
    return <Loading />;
  }

  return (
    <WebinarContext.Provider value={webinarContext}>
      <WebinarPropertiesProvider filter={{ ewebinar: { setId: webinarContext.setId } }}>
        {children}
      </WebinarPropertiesProvider>
    </WebinarContext.Provider>
  );
};
